import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import listItems from 'flarum/common/helpers/listItems';
import IndexPage from 'flarum/forum/components/IndexPage';
import WelcomeHero from 'flarum/forum/components/WelcomeHero';
import PageStructure from 'flarum/forum/components/PageStructure';
import ProfileCard, { profileEnabled } from './components/ProfileCard';
import TopContributors, { contributorsEnabled } from './components/TopContributors';
import { placedElsewhere } from './blocks';
import { markTagSeen } from './visits';
import { currentTag, t, ts } from './util';

/** fof/forum-widgets-core's side section, as it names its own item. */
const FOF_SIDE_ITEM = 'endWidgetSection';

/**
 * The index page as Central drew it: one card holding a title row (with the
 * sort dropdown) and the list, and a column of blocks on the right.
 */
export default function installIndexPage() {
  // The title row replaces the hero; the forum's welcome message moves into
  // the card (below), so an owner who wrote one does not lose it.
  override(IndexPage.prototype, 'hero', () => null);

  // The sidebar is the shell's now, on every page.
  override(IndexPage.prototype, 'sidebar', () => null);
  extend(IndexPage.prototype, 'contentItems', function (items) {
    const tag = currentTag();
    if (tag) markTagSeen(tag.id());
    items.remove('toolbar');
    items.add('concourseHead', m(PageHead, {
      page: this,
      current: tag
    }), 100);
    if (!tag && !app.search.state.params().q) {
      items.add('concourseWelcome', m("div", {
        className: "ConcourseWelcome"
      }, m(WelcomeHero, null)), 95);
    }
  });
  extend(PageStructure.prototype, 'containerItems', function (items) {
    if (!app.current.matches(IndexPage)) return;

    /*
     * fof/forum-widgets-core puts its side section in this list too. Left
     * alone that is a fourth column; adopted, the operator's side widgets
     * stack under Concourse's blocks in one right-hand column.
     */
    let side = null;
    if (items.has(FOF_SIDE_ITEM)) {
      side = items.get(FOF_SIDE_ITEM);
      items.remove(FOF_SIDE_ITEM);
    }
    const profile = profileEnabled() && !placedElsewhere('profile');
    const contributors = contributorsEnabled() && !placedElsewhere('contributors');
    if (!profile && !contributors && !side) return;
    items.add('concourseBlocks', m("aside", {
      className: "ConcourseBlocks",
      "aria-label": ts('blocks.label')
    }, profile ? m(ProfileCard, {
      host: "concourse"
    }) : null, contributors ? m(TopContributors, {
      host: "concourse"
    }) : null, side), 5);
  });
}

// 🚨 `current`, never `tag`: Flarum's Component refuses an attribute named
// `tag` (Mithril 2 reserves it) and throws on init.

/**
 * The card's title row: what this list is, then core's own toolbar — the
 * sort dropdown and anything other extensions add — kept rather than
 * rebuilt.
 */
class PageHead extends Component {
  view() {
    const {
      page,
      current: tag
    } = this.attrs;
    const following = (m.route.get() || '').startsWith('/following');
    const q = app.search.state.params().q;
    const children = tag ? app.store.all('tags').filter(other => other.parent?.() === tag && other.position() !== null) : [];
    let icon = m(Icon, {
      name: "fas fa-house",
      className: "ConcourseHead-icon"
    });
    let title = t('sidebar.home');
    if (tag) {
      icon = m("span", {
        className: "ConcourseHead-swatch",
        style: {
          '--tag-color': tag.color() || 'var(--cc-faint)'
        },
        "aria-hidden": "true"
      });
      title = tag.name();
    } else if (following) {
      icon = m(Icon, {
        name: "far fa-file-lines",
        className: "ConcourseHead-icon"
      });
      title = t('sidebar.following');
    } else if (q) {
      icon = m(Icon, {
        name: "fas fa-magnifying-glass",
        className: "ConcourseHead-icon"
      });
      title = t('head.search', {
        q
      });
    }
    return m("div", {
      className: "ConcourseHead"
    }, m("div", {
      className: "ConcourseHead-row"
    }, icon, m("div", {
      className: "ConcourseHead-text"
    }, m("h1", {
      className: "ConcourseHead-title"
    }, title), tag && tag.description() ? m("p", {
      className: "ConcourseHead-description"
    }, tag.description()) : null, children.length ? m("ul", {
      className: "ConcourseHead-children"
    }, children.map(child => m("li", null, m(Link, {
      href: app.route('tag', {
        tags: child.slug()
      }),
      className: "ConcourseChip"
    }, child.name())))) : null), m("div", {
      className: "ConcourseHead-tools"
    }, m("ul", {
      className: "ConcourseHead-actions"
    }, listItems(page.actionItems().toArray())), m("ul", {
      className: "ConcourseHead-view"
    }, listItems(page.viewItems().toArray())))));
  }
}